/**
 * Google Analytics 4 wrapper.
 *
 * Loads gtag.js lazily on first use, then exposes `pageview()` and
 * `event()` helpers. If REACT_APP_GA4_MEASUREMENT_ID is unset (dev
 * environments), every call is a silent no-op.
 */

const GA_ID = process.env.REACT_APP_GA4_MEASUREMENT_ID;
const CONSENT_KEY = "tbc.cookie_consent"; // "granted" | "denied"

let _loaded = false;

function hasConsent() {
  try {
    return typeof window !== "undefined" && window.localStorage.getItem(CONSENT_KEY) === "granted";
  } catch {
    return false;
  }
}

function loadGtag() {
  if (_loaded || typeof window === "undefined" || !GA_ID) return;
  if (!hasConsent()) return; // GDPR/CCPA — no tracking until user opts in
  _loaded = true;

  // Inject <script async src="https://www.googletagmanager.com/gtag/js?id=..."/>
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(s);

  // Initialize the dataLayer + gtag function
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () {
    window.dataLayer.push(arguments);
  };
  window.gtag("js", new Date());
  // send_page_view=false so we can fire pageviews manually on route changes
  // (single-page app — the default auto-pageview only fires on first load)
  window.gtag("config", GA_ID, { send_page_view: false });
}

export function isGaEnabled() {
  return !!GA_ID;
}

/** Called by the CookieConsentBanner. Grants tracking + boots gtag right away. */
export function grantConsent() {
  try { window.localStorage.setItem(CONSENT_KEY, "granted"); } catch { /* ignore */ }
  loadGtag();
}

/** Called by the CookieConsentBanner when the user declines analytics. */
export function denyConsent() {
  try { window.localStorage.setItem(CONSENT_KEY, "denied"); } catch { /* ignore */ }
}

/** Returns the current consent state so the banner can hide itself once set. */
export function getConsent() {
  try { return window.localStorage.getItem(CONSENT_KEY); } catch { return null; }
}

export function pageview(path) {
  if (!GA_ID || !hasConsent()) return;
  loadGtag();
  window.gtag?.("event", "page_view", {
    page_path: path,
    page_location: window.location.origin + path,
    page_title: document.title,
  });
}

export function event(name, params = {}) {
  if (!GA_ID || !hasConsent()) return;
  loadGtag();
  window.gtag?.("event", name, params);
}
